'use client'

import Reveal from '@/components/Reveal'
import {
  LandingShell, Hero, SystemsStrip, FeatureGrid, HowItWorks, InControl, FaqSection, FinalCta, PRICE_ANSWER,
} from '@/components/landing/ExtensionLanding'

// Landing page for people new to UK job applications (migrants, people moving
// to the UK): what UK forms ask, and how CVCircuit fills it in. Also the bio
// link for social posts aimed at newcomers. Claims stay within what the
// extension does: it enters what the Career Bank holds, it doesn't convert
// overseas qualifications or decide anyone's visa answers.

const SYSTEMS = [
  'NHS Jobs', 'Trac', 'Civil Service Jobs', 'Workday', 'Oleeo', 'Taleo', 'SAP SuccessFactors', 'iCIMS',
  'Greenhouse', 'Lever', 'Ashby', 'SmartRecruiters', 'Workable', 'Teamtailor', 'ApplyGateway',
]

export default function NewToUkPageClient() {
  return (
    <LandingShell>
      <Hero
        badge="New to UK job applications?"
        title="Applying for jobs in the UK,"
        highlight="without second-guessing every question."
        intro="UK application forms ask about your right to work, visa sponsorship, qualifications subject by subject, equality monitoring and two references, often on every form. Set your answers once in your Career Bank and CVCircuit fills them in the same way each time. You check each page and submit."
        example={{
          step: 'Right to work',
          label: "Example of a UK application form's right-to-work questions, filled in by CVCircuit",
          questions: [
            { q: 'Do you have the right to work in the UK?', a: 'The answer you set in your Career Bank' },
            { q: 'Will you now or in the future require visa sponsorship?', a: 'The answer you set in your Career Bank' },
            { q: 'Please give details of your visa or immigration status', a: 'Your status, as written in your Career Bank' },
            { q: 'Highest qualification', a: "Bachelor's degree" },
          ],
        }}
      />

      <FeatureGrid
        title="What UK forms ask"
        highlight="that other countries' often don't"
        intro="These questions trip people up on their first UK applications, and a rushed answer on your fifth form of the day can contradict the last one. Answer them carefully once, in your Career Bank."
        items={[
          { title: 'Right to work', body: 'Employers must check you can work in the UK before you start, so forms ask whether you can and often what your status is. CVCircuit gives the same answer on every form.' },
          { title: 'Visa sponsorship', body: "Many forms ask whether you'll need sponsorship now or in the future, for example on the Skilled Worker route. Set your answer in your Career Bank, especially if your visa has an end date." },
          { title: 'Share codes', body: "If you have an eVisa, employers may ask for a share code from GOV.UK to check your right to work. Codes expire, so CVCircuit leaves that box for you to fill in with a fresh one." },
          { title: 'Qualifications, subject by subject', body: "UK forms often want each qualification with its subject, grade and dates, in tables built for GCSEs and A-levels. CVCircuit enters yours exactly as you've recorded them." },
          { title: 'Equality monitoring', body: "Questions about ethnicity, religion, disability and more. They're usually optional and kept apart from your application. CVCircuit only answers what you've chosen to share." },
          { title: 'Two references', body: 'Most forms ask for two referees, often including your latest employer. Add them once, with contact details employers can reach, including referees outside the UK.' },
        ]}
      />

      {/* UK CVs */}
      <section className="bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-14 sm:py-20">
          <div className="max-w-4xl mx-auto grid gap-10 md:grid-cols-2 md:items-center">
            <Reveal>
              <h2 className="text-2xl sm:text-3xl font-bold">
                A UK CV looks <span className="text-primary-400">different, too</span>
              </h2>
              <p className="mt-4 text-gray-300">
                Many applications still ask for a CV alongside the form. UK employers expect a short, plain CV, and some things common elsewhere are usually left out.
              </p>
            </Reveal>
            <Reveal delayMs={120}>
              <ul className="space-y-4">
                {[
                  'Usually two pages, with your most recent job first.',
                  'No photo, date of birth or marital status.',
                  'A short personal profile at the top, written for the job.',
                  'CVCircuit helps you build a UK-style CV and tailor it to each job description.',
                ].map((line) => (
                  <li key={line} className="flex gap-3 text-gray-200">
                    <span className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-500/20 text-primary-300 text-xs">✓</span>
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <SystemsStrip
        heading="Tested on the application systems UK employers use"
        systems={SYSTEMS}
        note="It reads whatever form is on the page, so it works on many employers' own careers sites too."
      />

      <HowItWorks />
      <InControl />

      <FaqSection
        faqs={[
          { q: 'Will it answer the sponsorship question correctly for my visa?', a: "It answers from your Career Bank, so set both your right-to-work and sponsorship answers there, especially if your visa has an end date. If you're not sure how to answer for your visa, check GOV.UK or ask the employer, then save that answer once. Always check it before you submit." },
          { q: 'My qualifications are from outside the UK. Will it convert them?', a: "No. It enters your qualifications as you've recorded them in your Career Bank. If an employer asks for the UK equivalent, you can get a statement of comparability from Ecctis and record that alongside them." },
          { q: 'Does it fill in my share code?', a: "No. Share codes expire, so it leaves that box for you. Get a fresh code from GOV.UK's \"Prove your right to work\" service when an employer asks for one." },
          { q: 'What does it cost?', a: PRICE_ANSWER },
          { q: 'Does it work on my phone?', a: "Not yet. It's a Chrome extension, so it needs Google Chrome on a laptop or desktop computer. On a phone, you can email yourself the link to open later on your computer." },
          { q: 'What happens to my data?', a: 'Your Career Bank is stored in your CVCircuit account and used to fill in the forms you choose. We never sell your data.' },
        ]}
      />

      <FinalCta
        title="Fill in UK application forms with confidence"
        body="Add CVCircuit to Chrome, set up your Career Bank once, and every form gets the same careful answers."
      />
    </LandingShell>
  )
}
